import { useQuery, UseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArtistApiClient } from 'api';
import { useToast } from '@chakra-ui/react';
import { ArtistModel, ArtistIndexModel, ItemModel, ListingModel, PaginationModel } from 'models';
import { ArtistIndexType } from 'models/Artist/ArtistIndex';
import { ItemActiveMarketData } from 'models/Item/types';
import { JSONObject } from 'models/Api/types';

export const useArtist = (
  id: string,
  options?: Omit<UseQueryOptions<ArtistModel>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['artists', id],
    queryFn: () => ArtistApiClient.getArtistById(id),
    enabled: !!id,
    ...options,
  });
};

export const useArtistByItem = (
  itemId: string,
  options?: Omit<UseQueryOptions<ArtistModel>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['artists', 'byItem', itemId],
    queryFn: () => ArtistApiClient.getArtistByItemId(itemId),
    enabled: !!itemId,
    ...options,
  });
};

export const useArtists = () => {
  return useQuery({
    queryKey: ['artists'],
    queryFn: () => ArtistApiClient.getAllArtists(),
  });
};

export const useArtistItems = (
  id: string,
  options?: Omit<UseQueryOptions<ItemModel[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['artists', id, 'items'],
    queryFn: () => ArtistApiClient.getArtistItems(id),
    enabled: !!id,
    ...options,
  });
};

export const useArtistListings = (
  id: string,
  page: number,
  options?: Omit<UseQueryOptions<PaginationModel<ListingModel>>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['artists', id, 'listings', page],
    queryFn: () => ArtistApiClient.getArtistListings(id, page),
    enabled: !!id && page > 0,
    placeholderData: (previousData) => previousData,
    ...options,
  });
};

export const useArtistEditionIndex = (
  id: string,
  options?: Omit<UseQueryOptions<ArtistIndexModel>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['artists', id, 'editionIndex'],
    queryFn: () => ArtistApiClient.getArtistEditionIndex(id),
    enabled: !!id,
    ...options,
  });
};

export const useArtistActiveMarket = (
  id: string,
  options?: Omit<UseQueryOptions<ItemActiveMarketData>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['artists', id, 'activeMarket'],
    queryFn: () => ArtistApiClient.getArtistActiveMarket(id),
    enabled: !!id,
    ...options,
  });
};

type ArtistIndexMutationPayload = {
  artistId: string;
  type: ArtistIndexType;
};

export const useRegenerateArtistIndex = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ artistId, type }: ArtistIndexMutationPayload) =>
      ArtistApiClient.regenerateArtistIndex(artistId, type),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['artists', variables.artistId, 'editionIndex'] });
      toast({
        title: 'Index regeneration started',
        description: 'Edition index regeneration has been queued successfully.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to regenerate index',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });
};

export const useRefreshArtistIndex = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ artistId, type }: ArtistIndexMutationPayload) =>
      ArtistApiClient.refreshArtistIndex(artistId, type),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['artists', variables.artistId, 'editionIndex'] });
      toast({
        title: 'Index refresh started',
        description: 'Edition index refresh has been queued successfully.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to refresh index',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    },
  });
};

export const useUpdateArtist = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: JSONObject }) => ArtistApiClient.updateArtist(id, data),
    onSuccess: (updatedArtist) => {
      queryClient.invalidateQueries({ queryKey: ['artists'] });
      queryClient.setQueryData(['artists', updatedArtist.id], updatedArtist);
      toast({
        title: 'Artist updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update artist',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};

export const useCreateArtist = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: JSONObject) => ArtistApiClient.createArtist(data),
    onSuccess: (createdArtistId) => {
      queryClient.invalidateQueries({ queryKey: ['artists'] });
      toast({
        title: 'Artist created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      return createdArtistId;
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create artist',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};

export const useMigrateArtistImage = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, { id: string; imageUrl: string }>({
    mutationFn: ({ id, imageUrl }) => ArtistApiClient.migrateArtistImage(id, imageUrl),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['artists', variables.id] });
      toast({
        title: 'Image migrated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to migrate image',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};
